import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, Zap, Clock, Trash2, RefreshCw, Loader2, Download, HardDrive, FileWarning, Upload, CheckCircle2, CloudOff, CalendarDays, ChevronDown, ChevronRight, ShieldCheck, MailCheck, Archive as ArchiveIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtMs = (n) => (n == null ? '—' : `${Math.round(n)} ms`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
};

const DiagnosticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [migrating, setMigrating] = useState(false);

  // Storage diagnostic state
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageData, setStorageData] = useState(null);
  const [storageCheckCloud, setStorageCheckCloud] = useState(false);
  const [storageFilter, setStorageFilter] = useState('all');
  const [reuploadingId, setReuploadingId] = useState(null);
  const fileInputRefs = useRef({});

  // Monthly coverage state
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);

  // Scheduled runs (vigie automatique)
  const [runsData, setRunsData] = useState(null);
  const [runningHealthcheck, setRunningHealthcheck] = useState(false);
  const [runningArchive, setRunningArchive] = useState(false);

  const loadRuns = async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/admin/scheduled-runs`);
      setRunsData(res.data);
    } catch (e) {
      // silent — section is optional
    }
  };

  const triggerHealthcheck = async () => {
    setRunningHealthcheck(true);
    try {
      const res = await axios.post(`${BACKEND}/api/admin/scheduled-runs/healthcheck`);
      const r = res.data.result || {};
      if (r.missing_count > 0) {
        toast.warning(`⚠️ ${r.missing_count} PDF(s) manquant(s) — email envoyé : ${r.email_sent ? 'oui' : 'non'}`);
      } else {
        toast.success(`✅ Tous les ${r.checked} fichiers sont OK`);
      }
      await loadRuns();
      await loadStorage(storageCheckCloud);
      await loadMonthly();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du contrôle");
    } finally {
      setRunningHealthcheck(false);
    }
  };

  const triggerMonthlyArchive = async () => {
    const yyyymm = window.prompt("Mois à archiver (format YYYY-MM) — vide = mois précédent :", "");
    setRunningArchive(true);
    try {
      const params = yyyymm ? { yyyymm } : {};
      const res = await axios.post(`${BACKEND}/api/admin/scheduled-runs/monthly-archive`, null, { params });
      const r = res.data;
      toast.success(`✅ Archive ${r.yyyymm} : ${r.files_count} factures (${r.size_mb} Mo) — email : ${r.email_sent ? 'envoyé' : 'non envoyé'}`);
      await loadRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'archive");
    } finally {
      setRunningArchive(false);
    }
  };

  const downloadMonthlyArchive = (yyyymm) => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND}/api/admin/scheduled-runs/monthly-archive/${yyyymm}/download?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  const loadMonthly = async () => {
    setMonthlyLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/diagnostics/storage/monthly`, {
        params: { months: 24 },
      });
      setMonthlyData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur rapport mensuel");
    } finally {
      setMonthlyLoading(false);
    }
  };

  const loadStorage = async (checkCloud = false) => {
    setStorageLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/diagnostics/storage`, {
        params: { check_cloud: checkCloud },
      });
      setStorageData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du diagnostic stockage");
    } finally {
      setStorageLoading(false);
    }
  };

  const handleReupload = async (item, file) => {
    if (!file) return;
    if (file.type && file.type !== 'application/pdf') {
      toast.error("Le fichier doit être un PDF");
      return;
    }
    const key = `${item.kind}-${item.id}`;
    setReuploadingId(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${BACKEND}/api/admin/diagnostics/storage/reupload/${item.kind}/${item.id}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`✅ Fichier restauré : ${item.fichier_renomme || item.fichier_original || item.id}`);
      await loadStorage(storageCheckCloud);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du ré-upload");
    } finally {
      setReuploadingId(null);
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm("Lancer la migration des fichiers vers le stockage persistant ?\n\nCela peut prendre 30 secondes à 2 minutes selon le nombre de fichiers.")) return;
    setMigrating(true);
    try {
      const res = await axios.post(`${BACKEND}/api/admin/migrate-storage`);
      const s = res.data.stats || {};
      const total = (s.factures_artisans?.migrated || 0) + (s.factures_entreprise?.migrated || 0) + (s.devis_fournisseurs?.migrated || 0);
      toast.success(`Migration terminée : ${total} fichier(s) sauvegardé(s)`, { duration: 10000 });
      console.log('MIGRATION', s);
      alert(
        `✅ Migration terminée\n\n` +
        `Factures artisans : ${s.factures_artisans?.migrated || 0} migrées (${s.factures_artisans?.missing || 0} manquantes)\n` +
        `Factures entreprise : ${s.factures_entreprise?.migrated || 0} migrées (${s.factures_entreprise?.missing || 0} manquantes)\n` +
        `Devis fournisseurs : ${s.devis_fournisseurs?.migrated || 0} migrés (${s.devis_fournisseurs?.missing || 0} manquants)\n\n` +
        `Les fichiers "manquants" ont été perdus avant la migration et ne peuvent pas être récupérés.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la migration");
    } finally {
      setMigrating(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/diagnostics/summary`);
      setData(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement des diagnostics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadStorage(false); loadMonthly(); loadRuns(); }, []);

  const handleClear = async () => {
    if (!window.confirm("Effacer toutes les données de diagnostic ?")) return;
    setClearing(true);
    try {
      await axios.delete(`${BACKEND}/api/admin/diagnostics/clear`);
      toast.success("Données effacées");
      load();
    } catch (_) {
      toast.error("Erreur");
    } finally {
      setClearing(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/admin/diagnostics/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '');
      link.setAttribute('download', `diagnostics_maag_${ts}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rapport téléchargé');
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  return (
    <div className="space-y-6" data-testid="diagnostics-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Diagnostics</h1>
              <p className="text-sm text-slate-500">Erreurs et lenteurs des 7 derniers jours</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleMigrate}
              disabled={migrating}
              className="h-10 px-4 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg flex items-center gap-2 text-sm hover:bg-indigo-100 disabled:opacity-50"
              data-testid="migrate-storage-btn"
              title="Sauvegarder tous les PDF du serveur vers le stockage persistant (à faire 1 seule fois)"
            >
              {migrating ? <Loader2 className="w-4 h-4 animate-spin" /> : <HardDrive className="w-4 h-4" />}
              Migrer fichiers
            </button>
            <button
              onClick={load}
              className="h-10 px-4 bg-white border border-slate-300 rounded-lg flex items-center gap-2 text-sm hover:bg-slate-50"
              data-testid="refresh-diagnostics-btn"
            >
              <RefreshCw className="w-4 h-4" /> Rafraîchir
            </button>
            <button
              onClick={handleExport}
              className="h-10 px-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg flex items-center gap-2 text-sm hover:bg-emerald-100"
              data-testid="export-diagnostics-btn"
              title="Télécharger le rapport en .txt pour le transférer"
            >
              <Download className="w-4 h-4" /> Télécharger
            </button>
            <button
              onClick={handleClear}
              disabled={clearing}
              className="h-10 px-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2 text-sm hover:bg-red-100 disabled:opacity-50"
              data-testid="clear-diagnostics-btn"
            >
              {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Effacer
            </button>
          </div>
        </div>

        {loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {/* === STOCKAGE PDFS (priorité haute) === */}

        {/* Vigie automatique */}
        <Section
          title="Vigie automatique (sauvegardes & contrôles)"
          icon={<ShieldCheck className="w-5 h-5 text-emerald-600" />}
        >
          <div className="bg-emerald-50/50 border border-emerald-200 rounded-lg p-4 mb-4">
            <p className="text-xs text-slate-700 leading-relaxed">
              ✅ <b>Niveau 1 — Upload bloquant</b> : si le cloud refuse un upload, la facture n&apos;est PAS enregistrée (erreur explicite).<br/>
              ✅ <b>Niveau 2 — Contrôle quotidien</b> : tous les jours à 06h00, scan complet + email d&apos;alerte si fichier manquant détecté.<br/>
              ✅ <b>Niveau 3 — Sauvegarde mensuelle</b> : le 1er de chaque mois à 06h30, ZIP du mois précédent envoyé par email et stocké sur le cloud.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={triggerHealthcheck}
              disabled={runningHealthcheck}
              className="h-9 px-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg flex items-center gap-2 text-xs hover:bg-emerald-100 disabled:opacity-50"
              data-testid="trigger-healthcheck-btn"
            >
              {runningHealthcheck ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Lancer un contrôle maintenant
            </button>
            <button
              onClick={triggerMonthlyArchive}
              disabled={runningArchive}
              className="h-9 px-3 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg flex items-center gap-2 text-xs hover:bg-indigo-100 disabled:opacity-50"
              data-testid="trigger-archive-btn"
            >
              {runningArchive ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArchiveIcon className="w-4 h-4" />}
              Générer une archive mensuelle
            </button>
            <button
              onClick={loadRuns}
              className="h-9 px-3 bg-white border border-slate-300 rounded-lg flex items-center gap-2 text-xs hover:bg-slate-50"
              data-testid="reload-runs-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>

          {runsData?.runs?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase border-b">
                    <th className="py-2 px-2">Type</th>
                    <th className="py-2 px-2">Quand</th>
                    <th className="py-2 px-2">Détails</th>
                    <th className="py-2 px-2">Email</th>
                    <th className="py-2 px-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {runsData.runs.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="py-2 px-2 text-xs">
                        {r.kind === 'daily_healthcheck' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <ShieldCheck className="w-3 h-3" /> Contrôle quotidien
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-indigo-100 text-indigo-700 border border-indigo-200">
                            <ArchiveIcon className="w-3 h-3" /> Archive {r.yyyymm}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs text-slate-600 whitespace-nowrap">
                        {fmtDate(r.ran_at)}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {r.kind === 'daily_healthcheck' ? (
                          <span>
                            {r.checked} fichiers scannés,{' '}
                            <span className={r.missing_count > 0 ? 'text-red-600 font-semibold' : 'text-emerald-700'}>
                              {r.missing_count} manquant{r.missing_count !== 1 ? 's' : ''}
                            </span>
                          </span>
                        ) : (
                          <span>
                            {r.files_count} factures ({(r.size_bytes / 1024 / 1024).toFixed(2)} Mo)
                            {r.failed_files_count > 0 && (
                              <span className="text-amber-700 ml-2">⚠ {r.failed_files_count} non incluses</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {r.email_sent ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            <MailCheck className="w-3.5 h-3.5" /> Envoyé
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {r.kind === 'monthly_archive' && (
                          <button
                            onClick={() => downloadMonthlyArchive(r.yyyymm)}
                            className="px-2.5 py-1 rounded-md text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1.5"
                            data-testid={`download-archive-${r.yyyymm}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                            ZIP
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">Aucun run automatique enregistré pour le moment. Le premier contrôle aura lieu demain à 06h00.</p>
          )}
        </Section>

        {/* Monthly Coverage Report */}
        <Section
          title="Couverture mensuelle des PDFs (24 derniers mois)"
          icon={<CalendarDays className="w-5 h-5 text-emerald-500" />}
        >
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <p className="text-xs text-slate-500">
              Vérifie que chaque mois clos a 100% de ses factures sauvegardées sur le cloud. Cliquez sur un mois pour voir le détail des PDFs manquants.
            </p>
            <button
              onClick={loadMonthly}
              disabled={monthlyLoading}
              className="h-9 px-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg flex items-center gap-2 text-xs hover:bg-emerald-100 disabled:opacity-50"
              data-testid="monthly-refresh-btn"
            >
              {monthlyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Actualiser
            </button>
          </div>

          {monthlyLoading && (
            <div className="py-6 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}

          {!monthlyLoading && monthlyData && (
            <>
              <div className={`mb-4 px-4 py-3 rounded-lg border flex items-center gap-3 ${
                monthlyData.summary.missing === 0
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}>
                {monthlyData.summary.missing === 0
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  : <AlertTriangle className="w-5 h-5 text-amber-600" />
                }
                <div className="text-sm">
                  <span className="font-semibold">
                    Couverture globale : {monthlyData.summary.coverage_pct}%
                  </span>{' '}
                  ({monthlyData.summary.ok} / {monthlyData.summary.total} factures sauvegardées
                  {monthlyData.summary.missing > 0 && `, ${monthlyData.summary.missing} manquantes`})
                </div>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mb-4">
                {monthlyData.timeline.map((m) => {
                  const colors = {
                    ok: 'bg-emerald-100 border-emerald-300 text-emerald-800 hover:bg-emerald-200',
                    warning: 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200',
                    critical: 'bg-red-100 border-red-300 text-red-900 hover:bg-red-200',
                    empty: 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100',
                  };
                  const isExpanded = expandedMonth === m.month;
                  const clickable = m.missing_count > 0;
                  const monthLabel = new Date(`${m.month}-01`).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
                  return (
                    <button
                      key={m.month}
                      onClick={() => clickable && setExpandedMonth(isExpanded ? null : m.month)}
                      disabled={!clickable}
                      className={`px-2 py-2 rounded-lg border text-left transition-all ${colors[m.status]} ${
                        isExpanded ? 'ring-2 ring-indigo-400' : ''
                      } ${!clickable ? 'cursor-default' : 'cursor-pointer'}`}
                      data-testid={`monthly-cell-${m.month}`}
                    >
                      <div className="text-xs font-semibold capitalize">{monthLabel}</div>
                      {m.total === 0 ? (
                        <div className="text-[10px] opacity-60">—</div>
                      ) : (
                        <div className="text-[11px] mt-0.5">
                          {m.ok}/{m.total}
                          {m.missing_count > 0 && (
                            <span className="ml-1 font-bold">⚠</span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {expandedMonth && (() => {
                const m = monthlyData.timeline.find((x) => x.month === expandedMonth);
                if (!m || !m.missing || m.missing.length === 0) return null;
                const label = new Date(`${m.month}-01`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                return (
                  <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-amber-900 capitalize flex items-center gap-2">
                        <FileWarning className="w-4 h-4" />
                        {m.missing_count} PDF{m.missing_count > 1 ? 's' : ''} manquant{m.missing_count > 1 ? 's' : ''} — {label}
                      </h3>
                      <button
                        onClick={() => setExpandedMonth(null)}
                        className="text-xs text-amber-700 hover:underline"
                        data-testid="monthly-close-detail"
                      >
                        Fermer
                      </button>
                    </div>
                    <ul className="space-y-1 text-xs">
                      {m.missing.map((mi) => (
                        <li key={mi.id} className="flex items-center gap-2 text-slate-700">
                          <span className="font-mono bg-white border border-slate-200 px-1.5 rounded text-[10px]">
                            {mi.kind === 'facture_artisan' ? 'Artisan' : 'Entreprise'}
                          </span>
                          <span className="font-medium">{mi.fournisseur_nom || '—'}</span>
                          <span className="text-slate-500 ml-auto font-mono">
                            {mi.montant_ht != null ? `${Number(mi.montant_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[11px] text-amber-700 mt-3 italic">
                      💡 Utilisez le tableau &laquo;&nbsp;Diagnostic stockage des PDFs&nbsp;&raquo; ci-dessous pour ré-uploader ces fichiers.
                    </p>
                  </div>
                );
              })()}
            </>
          )}
        </Section>

        {/* Storage Diagnostic */}
        <Section
          title="Diagnostic stockage des PDFs"
          icon={<HardDrive className="w-5 h-5 text-indigo-500" />}
        >
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <p className="text-xs text-slate-500">
              Détecte les factures et devis dont le PDF physique est absent du cloud (métadonnées sauvegardées mais fichier perdu).
            </p>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={storageCheckCloud}
                  onChange={(e) => setStorageCheckCloud(e.target.checked)}
                  data-testid="storage-check-cloud-toggle"
                />
                Vérification cloud réelle (plus lent)
              </label>
              <button
                onClick={() => loadStorage(storageCheckCloud)}
                disabled={storageLoading}
                className="h-9 px-3 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg flex items-center gap-2 text-xs hover:bg-indigo-100 disabled:opacity-50"
                data-testid="storage-scan-btn"
              >
                {storageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Scanner
              </button>
            </div>
          </div>

          {storageLoading && (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}

          {!storageLoading && storageData && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <MiniStat label="Total scannés" value={storageData.totals?.scanned ?? 0} color="text-slate-700" />
                <MiniStat label="✅ OK Cloud" value={storageData.stats?.ok_cloud ?? 0} color="text-emerald-700" />
                <MiniStat label="💾 Local seul" value={storageData.stats?.ok_local ?? 0} color="text-amber-700" />
                <MiniStat label="☁️ Cloud manquant" value={storageData.stats?.cloud_missing ?? 0} color="text-orange-700" />
                <MiniStat label="❌ Orphelins" value={storageData.stats?.orphan ?? 0} color="text-red-700" />
              </div>

              {storageData.problems && storageData.problems.length > 0 ? (
                <>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-xs text-slate-500">Filtrer :</span>
                    {['all', 'orphan', 'cloud_missing', 'ok_local'].map((s) => (
                      <button
                        key={s}
                        onClick={() => setStorageFilter(s)}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                          storageFilter === s
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                        data-testid={`storage-filter-${s}`}
                      >
                        {s === 'all' ? 'Tous' : s === 'orphan' ? 'Orphelins' : s === 'cloud_missing' ? 'Cloud manquant' : 'Local seul'}
                      </button>
                    ))}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 uppercase border-b">
                          <th className="py-2 px-2">Type</th>
                          <th className="py-2 px-2">Statut</th>
                          <th className="py-2 px-2">Date</th>
                          <th className="py-2 px-2">Fournisseur</th>
                          <th className="py-2 px-2">Projet / Catégorie</th>
                          <th className="py-2 px-2 text-right">Montant HT</th>
                          <th className="py-2 px-2">Fichier d&apos;origine</th>
                          <th className="py-2 px-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {storageData.problems
                          .filter((p) => storageFilter === 'all' || p.status === storageFilter)
                          .map((p) => {
                            const key = `${p.kind}-${p.id}`;
                            const busy = reuploadingId === key;
                            return (
                              <tr key={key} className="hover:bg-slate-50">
                                <td className="py-2 px-2 text-xs">
                                  <span className="font-mono bg-slate-100 px-1.5 rounded">
                                    {p.kind === 'facture_artisan' ? 'Artisan' : p.kind === 'facture_entreprise' ? 'Entreprise' : 'Devis'}
                                  </span>
                                </td>
                                <td className="py-2 px-2">
                                  <StatusBadge status={p.status} />
                                </td>
                                <td className="py-2 px-2 text-xs whitespace-nowrap">{p.date_facture || '—'}</td>
                                <td className="py-2 px-2 font-medium text-slate-800">{p.fournisseur_nom || '—'}</td>
                                <td className="py-2 px-2 text-xs text-slate-600">
                                  {p.projet_nom || p.categorie || '—'}
                                </td>
                                <td className="py-2 px-2 text-right text-xs font-mono">
                                  {p.montant_ht != null ? `${Number(p.montant_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—'}
                                </td>
                                <td className="py-2 px-2 text-xs text-slate-500 truncate max-w-[200px]" title={p.fichier_original || p.fichier_renomme}>
                                  {p.fichier_original || p.fichier_renomme || '—'}
                                </td>
                                <td className="py-2 px-2 text-right">
                                  <input
                                    ref={(el) => { fileInputRefs.current[key] = el; }}
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    onChange={(e) => handleReupload(p, e.target.files?.[0])}
                                    data-testid={`storage-reupload-input-${key}`}
                                  />
                                  <button
                                    onClick={() => fileInputRefs.current[key]?.click()}
                                    disabled={busy}
                                    className="px-2.5 py-1 rounded-md text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1.5 ml-auto disabled:opacity-50"
                                    data-testid={`storage-reupload-btn-${key}`}
                                  >
                                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                    Ré-uploader
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3 text-sm text-emerald-800">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Tous les fichiers sont correctement stockés 🎉
                </div>
              )}
            </>
          )}
        </Section>

        {/* === TELEMETRY (priorité basse, en bas de page) === */}

        {!loading && data && (
          <>
            {/* Totals */}
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Événements" value={data.totals.events} icon={<Activity className="w-5 h-5 text-slate-500" />} />
              <StatCard label="Erreurs" value={data.totals.errors} icon={<AlertTriangle className="w-5 h-5 text-red-500" />} color="text-red-700" />
              <StatCard label="Appels lents" value={data.totals.slow_calls} icon={<Clock className="w-5 h-5 text-amber-500" />} color="text-amber-700" />
            </div>

            {/* Slow endpoints */}
            <Section title="Endpoints API les plus lents" icon={<Zap className="w-5 h-5 text-amber-500" />}>
              {data.slow_endpoints.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Aucun endpoint lent détecté 🎉</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase border-b">
                      <th className="py-2 px-2">Méthode</th>
                      <th className="py-2 px-2">Chemin</th>
                      <th className="py-2 px-2 text-right">Occurrences</th>
                      <th className="py-2 px-2 text-right">Moyenne</th>
                      <th className="py-2 px-2 text-right">Max</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.slow_endpoints.map((ep, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="py-2 px-2"><span className="font-mono text-xs bg-slate-100 px-1.5 rounded">{ep.method}</span></td>
                        <td className="py-2 px-2 font-mono text-xs">{ep.path}</td>
                        <td className="py-2 px-2 text-right">{ep.count}</td>
                        <td className="py-2 px-2 text-right font-medium text-amber-700">{fmtMs(ep.avg_ms)}</td>
                        <td className="py-2 px-2 text-right text-red-700">{fmtMs(ep.max_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Slow pages */}
            <Section title="Pages les plus lentes" icon={<Clock className="w-5 h-5 text-indigo-500" />}>
              {data.slow_pages.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Pas encore de mesures de chargement</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase border-b">
                      <th className="py-2 px-2">Route</th>
                      <th className="py-2 px-2 text-right">Visites</th>
                      <th className="py-2 px-2 text-right">Moyenne</th>
                      <th className="py-2 px-2 text-right">Max</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.slow_pages.map((p, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="py-2 px-2 font-mono text-xs">{p.route}</td>
                        <td className="py-2 px-2 text-right">{p.count}</td>
                        <td className="py-2 px-2 text-right font-medium text-indigo-700">{fmtMs(p.avg_ms)}</td>
                        <td className="py-2 px-2 text-right text-red-700">{fmtMs(p.max_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Recent errors */}
            <Section title="Erreurs récentes" icon={<AlertTriangle className="w-5 h-5 text-red-500" />}>
              {data.recent_errors.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Aucune erreur récente 🎉</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {data.recent_errors.map((e) => (
                    <div key={e.id} className="border border-red-100 bg-red-50/50 rounded-lg p-3 text-sm">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-mono text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{e.kind}</span>
                        <span className="text-xs text-slate-500">{fmtDate(e.created_at)}</span>
                      </div>
                      {e.path && (
                        <div className="mt-1 text-xs text-slate-700">
                          <span className="font-mono bg-slate-100 px-1 rounded">{e.method || 'GET'} {e.path}</span>
                          {e.status_code ? <span className="ml-2 text-red-700 font-semibold">→ {e.status_code}</span> : null}
                        </div>
                      )}
                      {e.route && e.route !== e.path && (
                        <div className="mt-0.5 text-xs text-slate-500">Page : {e.route}</div>
                      )}
                      {e.message && <div className="mt-1 text-sm text-slate-900">{e.message}</div>}
                      {e.stack && (
                        <details className="mt-1">
                          <summary className="text-xs text-slate-500 cursor-pointer">Trace</summary>
                          <pre className="text-[10px] text-slate-700 bg-white rounded p-2 mt-1 overflow-auto max-h-40">{e.stack}</pre>
                        </details>
                      )}
                      {e.user_email && <div className="mt-1 text-xs text-slate-500">👤 {e.user_email}</div>}
                    </div>
                  ))}
                </div>
              )}
            </Section>

          </>
        )}
      </div>
  );
};

const StatCard = ({ label, value, icon, color = 'text-slate-900' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
    <div>{icon}</div>
    <div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  </div>
);

const Section = ({ title, icon, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">{icon} {title}</h2>
    {children}
  </div>
);

const MiniStat = ({ label, value, color = 'text-slate-700' }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    <p className="text-[11px] text-slate-500 leading-tight">{label}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    ok_cloud: { label: 'OK cloud', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
    ok_local: { label: 'Local seul', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: HardDrive },
    cloud_missing: { label: 'Cloud manquant', cls: 'bg-orange-100 text-orange-700 border-orange-200', Icon: CloudOff },
    orphan: { label: 'Orphelin', cls: 'bg-red-100 text-red-700 border-red-200', Icon: FileWarning },
    unknown: { label: 'Inconnu', cls: 'bg-slate-100 text-slate-700 border-slate-200', Icon: AlertTriangle },
  };
  const { label, cls, Icon } = map[status] || map.unknown;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${cls}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

export default DiagnosticsPage;
